import type { AliasAgeRating, AliasDifficulty, AliasWordEntry, AliasWordPackId } from './alias-types';

type WordSeed = readonly [text: string, category: string, difficulty?: AliasDifficulty, ageRating?: AliasAgeRating];

function pack(packId: AliasWordPackId, values: readonly WordSeed[]): AliasWordEntry[] {
  return values.map(([text, category, difficulty = 'medium', ageRating = '6+'], index) => ({
    id: `alias_${packId}_${String(index + 1).padStart(3, '0')}`,
    text,
    category,
    packId,
    difficulty,
    ageRating,
  }));
}

/**
 * Original common-word deck for the first Alias release. These are deliberately
 * ordinary words and concepts, not copied commercial game cards.
 */
export const ALIAS_WORDS: AliasWordEntry[] = [
  ...pack('base', [
    ['Зонтик', 'Предмет', 'easy'], ['Зеркало', 'Предмет', 'easy'], ['Будильник', 'Предмет', 'easy'],
    ['Холодильник', 'Предмет', 'easy'], ['Рюкзак', 'Предмет', 'easy'], ['Подушка', 'Предмет', 'easy'],
    ['Лестница', 'Предмет', 'easy'], ['Фонарь', 'Предмет', 'easy'], ['Карандаш', 'Предмет', 'easy'],
    ['Пылесос', 'Предмет', 'easy'], ['Почтальон', 'Профессия', 'easy'], ['Повар', 'Профессия', 'easy'],
    ['Космонавт', 'Профессия', 'medium'], ['Фотограф', 'Профессия', 'easy'], ['Водопад', 'Природа', 'easy'],
    ['Вулкан', 'Природа', 'easy'], ['Остров', 'Природа', 'easy'], ['Радуга', 'Природа', 'easy'],
    ['Шёпот', 'Действие', 'medium'], ['Аплодисменты', 'Действие', 'easy'], ['Путешествие', 'Понятие', 'easy'],
    ['Сюрприз', 'Понятие', 'easy'], ['Очередь', 'Понятие', 'medium'], ['Праздник', 'Понятие', 'easy'],
    ['Светофор', 'Город', 'easy'], ['Самокат', 'Транспорт', 'easy'], ['Парашют', 'Предмет', 'medium'],
    ['Маяк', 'Место', 'medium'], ['Велосипед', 'Транспорт', 'easy'], ['Пианино', 'Музыка', 'easy'],
  ]),
  ...pack('family', [
    ['Котёнок', 'Животные', 'easy'], ['Черепаха', 'Животные', 'easy'], ['Жираф', 'Животные', 'easy'],
    ['Дельфин', 'Животные', 'easy'], ['Пингвин', 'Животные', 'easy'], ['Белка', 'Животные', 'easy'],
    ['Бабочка', 'Животные', 'easy'], ['Крокодил', 'Животные', 'easy'], ['Попугай', 'Животные', 'easy'],
    ['Единорог', 'Сказки', 'easy'], ['Мороженое', 'Еда', 'easy'], ['Пицца', 'Еда', 'easy'],
    ['Шоколад', 'Еда', 'easy'], ['Арбуз', 'Еда', 'easy'], ['Омлет', 'Еда', 'easy'],
    ['Карусель', 'Развлечения', 'easy'], ['Конструктор', 'Игры', 'easy'], ['Прятки', 'Игры', 'easy'],
    ['Рисование', 'Хобби', 'easy'], ['Воздушный змей', 'Развлечения', 'medium'], ['День рождения', 'Праздник', 'easy'],
    ['Снеговик', 'Зима', 'easy'], ['Песочница', 'Двор', 'easy'], ['Аквариум', 'Дом', 'easy'],
    ['Качели', 'Двор', 'easy'], ['Пожарная машина', 'Транспорт', 'easy'], ['Доктор', 'Профессия', 'easy'],
    ['Цирк', 'Место', 'easy'], ['Сокровище', 'Приключения', 'easy'], ['Волшебная палочка', 'Сказки', 'easy'],
  ]),
  ...pack('travel', [
    ['Аэропорт', 'Путешествия', 'easy'], ['Чемодан', 'Путешествия', 'easy'], ['Паспорт', 'Путешествия', 'easy'],
    ['Карта', 'Путешествия', 'easy'], ['Отель', 'Путешествия', 'easy'], ['Пляж', 'Путешествия', 'easy'],
    ['Палатка', 'Туризм', 'easy'], ['Компас', 'Туризм', 'medium'], ['Бинокль', 'Туризм', 'easy'],
    ['Экскурсия', 'Путешествия', 'medium'], ['Сувенир', 'Путешествия', 'easy'], ['Поезд', 'Транспорт', 'easy'],
    ['Метро', 'Транспорт', 'easy'], ['Такси', 'Транспорт', 'easy'], ['Корабль', 'Транспорт', 'easy'],
    ['Горнолыжник', 'Спорт', 'medium'], ['Путеводитель', 'Путешествия', 'medium'], ['Граница', 'Путешествия', 'medium'],
    ['Виза', 'Путешествия', 'medium'], ['Ресторан', 'Город', 'easy'], ['Рынок', 'Город', 'easy'],
    ['Музей', 'Город', 'easy'], ['Площадь', 'Город', 'easy'], ['Набережная', 'Город', 'medium'],
    ['Канатная дорога', 'Транспорт', 'medium'], ['Кемпинг', 'Туризм', 'medium'], ['Закат', 'Природа', 'easy'],
    ['Панорама', 'Путешествия', 'medium'], ['Прокат', 'Путешествия', 'medium'], ['Приключение', 'Путешествия', 'easy'],
  ]),
  ...pack('culture', [
    ['Театр', 'Культура', 'easy', '10+'], ['Оркестр', 'Музыка', 'medium', '10+'], ['Мелодия', 'Музыка', 'easy', '10+'],
    ['Балет', 'Культура', 'medium', '10+'], ['Роман', 'Литература', 'medium', '10+'], ['Комикс', 'Литература', 'easy', '10+'],
    ['Библиотека', 'Культура', 'easy', '10+'], ['Режиссёр', 'Кино', 'medium', '10+'], ['Микрофон', 'Музыка', 'easy', '10+'],
    ['Спектакль', 'Культура', 'medium', '10+'], ['Галерея', 'Культура', 'medium', '10+'], ['Премьера', 'Культура', 'medium', '10+'],
    ['Скульптура', 'Искусство', 'medium', '10+'], ['Портрет', 'Искусство', 'easy', '10+'], ['Сюжет', 'Кино', 'medium', '10+'],
    ['Интервью', 'Медиа', 'medium', '10+'], ['Репетиция', 'Культура', 'medium', '10+'], ['Костюм', 'Культура', 'easy', '10+'],
    ['Сцена', 'Культура', 'easy', '10+'], ['Билет', 'Культура', 'easy', '10+'], ['Фестиваль', 'Культура', 'easy', '10+'],
    ['Шедевр', 'Искусство', 'hard', '12+'], ['Метафора', 'Литература', 'hard', '12+'], ['Сатира', 'Литература', 'hard', '12+'],
    ['Документальный фильм', 'Кино', 'medium', '10+'], ['Мультфильм', 'Кино', 'easy', '10+'], ['Кинотеатр', 'Кино', 'easy', '10+'],
    ['Афиша', 'Культура', 'medium', '10+'], ['Танец', 'Культура', 'easy', '10+'], ['Хор', 'Музыка', 'easy', '10+'],
  ]),
  ...pack('work', [
    ['Программист', 'Профессия', 'medium', '12+'], ['Архитектор', 'Профессия', 'medium', '12+'], ['Учитель', 'Профессия', 'easy', '12+'],
    ['Переводчик', 'Профессия', 'medium', '12+'], ['Инженер', 'Профессия', 'medium', '12+'], ['Дизайнер', 'Профессия', 'medium', '12+'],
    ['Редактор', 'Профессия', 'medium', '12+'], ['Спасатель', 'Профессия', 'easy', '12+'], ['Фермер', 'Профессия', 'easy', '12+'],
    ['Планёрка', 'Работа', 'medium', '12+'], ['Проект', 'Работа', 'easy', '12+'], ['Дедлайн', 'Работа', 'medium', '12+'],
    ['Переговоры', 'Работа', 'medium', '12+'], ['Презентация', 'Работа', 'medium', '12+'], ['Команда', 'Работа', 'easy', '12+'],
    ['Собеседование', 'Работа', 'medium', '12+'], ['Клиент', 'Работа', 'easy', '12+'], ['Стажировка', 'Работа', 'medium', '12+'],
    ['Мастерская', 'Работа', 'easy', '12+'], ['Лаборатория', 'Наука', 'medium', '12+'], ['Робот', 'Технологии', 'easy', '12+'],
    ['Алгоритм', 'Технологии', 'hard', '12+'], ['Изобретение', 'Наука', 'medium', '12+'], ['Электростанция', 'Технологии', 'medium', '12+'],
    ['Спутник', 'Космос', 'medium', '12+'], ['Телескоп', 'Наука', 'medium', '12+'], ['Каска', 'Профессия', 'easy', '12+'],
    ['Чертёж', 'Работа', 'medium', '12+'], ['Курьер', 'Профессия', 'easy', '12+'], ['Строитель', 'Профессия', 'easy', '12+'],
  ]),
];

export const ALIAS_WORD_PACK_NAMES: Record<AliasWordPackId, string> = {
  base: 'Базовый',
  family: 'Семейный',
  travel: 'Путешествия',
  culture: 'Культура',
  work: 'Профессии и технологии',
};

